import { useData } from "../../runtime/provider";
import { albumsQuery } from "../../data/queries/collections";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { infiniteDataSchema } from "@utils/durableCache/schemas";
import { cachedAlbums } from "@sqlite/schema";
import { albumSchema, type Album } from "@libs/wire";
import {
  albumsByGenrePageWireSchema,
  contentByGenrePageWireSchema,
} from "@libs/wire";
import {
  type UseQueryOptionsFromConfig,
  type UseSuspenseQueryOptionsFromConfig,
} from "@utils/queryConfigs";
import { trpc, type RouterOutputs } from "@utils/trpc";
import type { z } from "zod";

/**
 * Bidirectional drift check vs the wire shape — see `useSongs.ts` for the
 * rationale.
 */
type _AlbumRouterShape = RouterOutputs["catalog"]["albums"]["getById"][number];
type _AlbumWireSchemaSubset = Album extends _AlbumRouterShape ? true : never;
type _AlbumWireSchemaSuperset = _AlbumRouterShape extends Album ? true : never;
const _albumWireSchemaDriftCheck: [
  _AlbumWireSchemaSubset,
  _AlbumWireSchemaSuperset,
] = [true, true];
void _albumWireSchemaDriftCheck;

type AlbumsQueryConfig = ReturnType<typeof albumsQuery>;
type QueryOptions = UseQueryOptionsFromConfig<AlbumsQueryConfig>;
type SuspenseQueryOptions =
  UseSuspenseQueryOptionsFromConfig<AlbumsQueryConfig>;

export { albumsQuery, albumsQueryKey } from "../../data/queries/collections";

export const useAlbums = (ids?: string[], options?: QueryOptions) => {
  const data = useData();
  const query = useQuery({
    ...albumsQuery(data, ids),
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });

  return { query };
};

export const useAlbumsSuspense = (ids?: string[], options?: QueryOptions) => {
  const data = useData();
  const query = useSuspenseQuery({
    ...albumsQuery(data, ids),
    ...(options as SuspenseQueryOptions | undefined),
  });

  return { query };
};

type AlbumsByGenrePage = RouterOutputs["catalog"]["albums"]["getByGenreId"];

// One-way drift check: see useForYou for rationale.
type _AlbumsByGenreWireSchemaCheck =
  AlbumsByGenrePage extends z.infer<typeof albumsByGenrePageWireSchema>
    ? true
    : never;
const _albumsByGenreWireSchemaDriftCheck: _AlbumsByGenreWireSchemaCheck = true;
void _albumsByGenreWireSchemaDriftCheck;

const albumsByGenreInfiniteSchema = infiniteDataSchema(
  albumsByGenrePageWireSchema,
  albumsByGenrePageWireSchema.shape.endCursor,
);

export const useAlbumsByGenre = (genreId: string, options?: any) => {
  const data = useData();
  const input = { genre_id: genreId, limit: 12 };
  const queryKey = getQueryKey(
    trpc.catalog.albums.getByGenreId,
    input,
    "infinite",
  );
  const query = trpc.catalog.albums.getByGenreId.useInfiniteQuery(input, {
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
    getNextPageParam: (lastPage: { endCursor?: number }) => {
      return lastPage.endCursor;
    },
    getPreviousPageParam: (firstPage: { startCursor?: number }) => {
      return firstPage.startCursor;
    },
    initialCursor: undefined,
    staleTime: 60 * 60 * 1000, // 1 hour — genre catalog is slow-changing
    ...data.cache.durableInfiniteOptions<AlbumsByGenrePage>(
      queryKey,
      albumsByGenreInfiniteSchema,
      {
        // Pre-warm `cached_albums` from genre feed pages so subsequent
        // `useAlbums([id])` resolves from the per-entity cache.
        decompose: (data: InfiniteData<AlbumsByGenrePage>) =>
          data.pages.flatMap((p) => p.data),
        decomposeTable: cachedAlbums,
        decomposeIdField: "id",
        decomposeSchema: albumSchema,
      },
    ),
  });

  return { query };
};

type ContentByGenrePage =
  RouterOutputs["catalog"]["listContentByGenrePaginated"];

type _ContentByGenreWireSchemaCheck =
  ContentByGenrePage extends z.infer<typeof contentByGenrePageWireSchema>
    ? true
    : never;
const _contentByGenreWireSchemaDriftCheck: _ContentByGenreWireSchemaCheck = true;
void _contentByGenreWireSchemaDriftCheck;

const contentByGenreInfiniteSchema = infiniteDataSchema(
  contentByGenrePageWireSchema,
  contentByGenrePageWireSchema.shape.endCursor,
);

export const useContentByGenre = (genreId: string, options?: any) => {
  const data = useData();
  const input = { genre_id: genreId, limit: 12 };
  const queryKey = getQueryKey(
    trpc.catalog.listContentByGenrePaginated,
    input,
    "infinite",
  );
  const query = trpc.catalog.listContentByGenrePaginated.useInfiniteQuery(
    input,
    {
      ...options,
      enabled: options?.enabled !== undefined ? options?.enabled : true,
      getNextPageParam: (lastPage: { endCursor?: number }) => {
        return lastPage.endCursor;
      },
      getPreviousPageParam: (firstPage: { startCursor?: number }) => {
        return firstPage.startCursor;
      },
      initialCursor: undefined,
      staleTime: 60 * 60 * 1000, // 1 hour — genre catalog is slow-changing
      ...data.cache.durableInfiniteOptions<ContentByGenrePage>(
        queryKey,
        contentByGenreInfiniteSchema,
        {
          // Mixed (album|playlist) feed — only album-typed items have a
          // per-entity table to pre-warm. Filter playlists out of the
          // decomposed list. (When `cached_playlists` lands, this can be
          // extended to a multi-table decompose.)
          decompose: (data: InfiniteData<ContentByGenrePage>) =>
            data.pages
              .flatMap((p) => p.data)
              .filter(
                (item): item is Album & { type: "album" } =>
                  (item as { type: string }).type === "album",
              ),
          decomposeTable: cachedAlbums,
          decomposeIdField: "id",
          decomposeSchema: albumSchema,
        },
      ),
    },
  );

  return { query };
};
